import { useState } from 'react';
import { Link } from 'react-router-dom';
import { projects } from '@/mocks/projects';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppButton from '@/components/feature/WhatsAppButton';
import { useReveal } from '@/hooks/useReveal';

type Project = (typeof projects)[number];

const filters = ['Todos', 'Residencial', 'Comercial', 'Interior'] as const;
type Filter = (typeof filters)[number];

function ProjectCard({ project }: { project: Project }) {
  return (
    <Link
      to={`/proyectos/${project.slug}`}
      className="group block reveal break-inside-avoid mb-10 md:mb-14"
    >
      <div className="relative overflow-hidden bg-neutral-100">
        <img
          src={project.image}
          alt={`${project.name} — diseño arquitectónico ${project.category.toLowerCase()} en ${project.zone}`}
          title={`${project.name} · ${project.category} · San Luis Potosí`}
          className="block w-full h-auto transition-transform duration-700 group-hover:scale-[1.04]"
        />
        <span className="absolute top-4 left-4 w-10 h-10 border-t border-l border-white/50"></span>
      </div>
      <div className="mt-5 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h3 className="font-display text-xl font-medium tracking-tightest group-hover:opacity-60 transition-opacity">
          {project.name}
        </h3>
        <span className="overline text-neutral-400">{project.year}</span>
      </div>
      <p className="text-sm text-neutral-500 mt-2">
        {project.category} · {project.zone} · {project.area}
      </p>
    </Link>
  );
}

export default function Proyectos() {
  useReveal();
  const [active, setActive] = useState<Filter>('Todos');

  const visible =
    active === 'Todos' ? projects : projects.filter((p) => p.category === active);

  return (
    <>
      <Navbar />
      <main className="pt-24 md:pt-32">
        <div className="max-w-[1600px] mx-auto px-5 md:px-10">
          <div className="mb-10 md:mb-14">
            <p className="overline text-neutral-400 reveal">Portafolio</p>
            <h1 className="font-display font-medium tracking-tightest text-[clamp(2.5rem,7vw,6rem)] mt-4 reveal">
              Proyectos
            </h1>
            <p className="max-w-2xl text-neutral-600 mt-6 leading-relaxed reveal">
              Una selección de proyectos de diseño arquitectónico, interiores y
              espacios comerciales en San Luis Potosí y sus alrededores.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-12 reveal">
            {filters.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setActive(f)}
                className={`whitespace-nowrap px-5 py-2.5 text-xs font-medium uppercase tracking-[0.12em] border transition-colors ${
                  active === f
                    ? 'bg-neutral-900 text-white border-neutral-900'
                    : 'text-neutral-600 border-neutral-300 hover:border-neutral-900 hover:text-neutral-900'
                }`}
              >
                {f}
              </button>
            ))}
          </div>

          <div className="columns-1 md:columns-2 gap-8 md:gap-10 pb-24 md:pb-32">
            {visible.map((p) => (
              <ProjectCard key={p.slug} project={p} />
            ))}
          </div>
        </div>
      </main>
      <Footer />
      <WhatsAppButton />
    </>
  );
}